/**
 * The locales the site knows about, and the single switch that decides which of
 * them a visitor can actually reach.
 *
 * Polish is the only one shipped: every route lives under `/pl/*` and the copy
 * is written, not translated. English is registered here rather than left out
 * so that turning it on is `available: true` plus the routes — not a hunt for
 * every place a language is assumed. Until then the switcher shows it as
 * coming, which is true, instead of linking to a 404.
 *
 * This is deliberately NOT an i18n library. `next-intl` lands with the "full
 * app" milestone; when it does, `routing.locales` replaces this list and
 * `withLocale` gives way to its navigation helpers.
 */

export const LOCALES = [
  // Endonyms — a language names itself in its own words, never in the reader's.
  { code: 'pl', name: 'Polski', available: true },
  { code: 'en', name: 'English', available: false },
] as const

export type LocaleCode = (typeof LOCALES)[number]['code']

const CODES: readonly string[] = LOCALES.map((locale) => locale.code)

/**
 * The same page in another language.
 *
 * Swaps the leading locale segment rather than sending the visitor home: a
 * language switch that drops someone from the pricing page onto the landing
 * page has lost their place, which is the one thing it must not do.
 */
export function withLocale(pathname: string, locale: LocaleCode): string {
  const segments = pathname.split('/').filter(Boolean)

  if (segments.length > 0 && CODES.includes(segments[0])) {
    segments[0] = locale
  } else {
    segments.unshift(locale)
  }

  return `/${segments.join('/')}`
}
